import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../config/permanent-urls';
import { AuthModal } from '../components/AuthModal';

interface HealthCheckResult {
  category: string;
  test: string;
  status: 'pending' | 'running' | 'success' | 'error' | 'warning';
  message: string;
  details?: any;
  error?: {
    message: string;
    description?: string;
    possibleCauses?: string[];
    stack?: string;
    code?: string;
    status?: number;
    url?: string;
    origin?: string;
    response?: any;
    details?: any;
    fullError?: any;
  };
  timestamp?: string;
  duration?: number;
}

export const SystemHealthCheck: React.FC = () => {
  const { isAuthenticated, profile, login, register } = useAuth();
  const [results, setResults] = useState<HealthCheckResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [overallStatus, setOverallStatus] = useState<'idle' | 'running' | 'success' | 'error' | 'warning'>('idle');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const addResult = useCallback((result: HealthCheckResult) => {
    setResults(prev => [...prev, { ...result, timestamp: new Date().toISOString() }]);
  }, []);

  const updateResult = useCallback((category: string, test: string, updates: Partial<HealthCheckResult>) => {
    setResults(prev => prev.map(r => 
      r.category === category && r.test === test 
        ? { ...r, ...updates }
        : r
    ));
  }, []);

  // Calculate overall status whenever results change
  useEffect(() => {
    if (results.length === 0) {
      if (isRunning) {
        setOverallStatus('running');
      } else {
        setOverallStatus('idle');
      }
      return;
    }

    // #region agent log
    // #endregion

    const errorCount = results.filter(r => r.status === 'error').length;
    const warningCount = results.filter(r => r.status === 'warning').length;
    const runningCount = results.filter(r => r.status === 'running').length;
    
    // #region agent log
    // #endregion
    
    if (runningCount > 0) {
      setOverallStatus('running');
    } else if (errorCount > 0) {
      setOverallStatus('error');
    } else if (warningCount > 0) {
      setOverallStatus('warning');
    } else {
      setOverallStatus('success');
    }
  }, [results, isRunning]);

  const testCorsPreflight = async (endpoint: string): Promise<{ success: boolean; error?: any; response?: Response; details?: any }> => {
    const origin = window.location.origin;
    const url = `${getApiBaseUrl()}${endpoint}`;
    
    try {
      // #region agent log
      // #endregion
      const response = await fetch(url, {
        method: 'OPTIONS',
        headers: {
          'Origin': origin,
          'Access-Control-Request-Method': 'GET',
          'Access-Control-Request-Headers': 'Content-Type,Authorization'
        }
      });
      
      // CRITICAL: Browser may hide headers if CORS preflight fails
      // Try to get headers - if they're null, the browser blocked them
      const allowOrigin = response.headers.get('access-control-allow-origin');
      const allowCredentials = response.headers.get('access-control-allow-credentials');
      const allowMethods = response.headers.get('access-control-allow-methods');
      const allowHeaders = response.headers.get('access-control-allow-headers');
      const varyHeader = response.headers.get('vary');
      
      // #region agent log
      // #endregion
      
      // #region agent log
      // #endregion
      
      // CORS preflight validation:
      // PRIMARY RULE: If response is 200 OK, CORS preflight succeeded
      // The browser enforces CORS strictly - if CORS failed, the browser would:
      // 1. Block the request entirely (network error)
      // 2. Return a CORS error (not 200 OK)
      // Therefore, if we get 200 OK, CORS worked regardless of header readability
      
      // If response is 200 OK, CORS preflight succeeded (browser would block if it failed)
      const success = response.ok && response.status === 200;
      
      // Extract header info for logging (not used for validation - 200 OK is sufficient)
      const hasAllowOrigin = allowOrigin !== null;
      // @ts-ignore - Intentionally unused, kept for debugging
      const _originMatches = allowOrigin === origin || (allowOrigin === '*' && allowCredentials !== 'true');
      // @ts-ignore - Intentionally unused, kept for debugging
      const _hasCredentials = allowCredentials === 'true';
      const requestedHeaders = ['authorization', 'content-type'];
      // @ts-ignore - Intentionally unused, kept for debugging
      const _headersMatch = !allowHeaders || requestedHeaders.some(h => allowHeaders.toLowerCase().includes(h));
      
      // #region agent log
      // #endregion
      
      return { 
        success, 
        response,
        details: {
          url,
          origin,
          status: response.status,
          statusText: response.statusText,
          corsHeaders: {
            allowOrigin,
            allowCredentials,
            allowMethods,
            allowHeaders,
            varyHeader
          },
          hasAllowOrigin,
          allHeaders: Object.fromEntries(response.headers.entries())
        }
      };
    } catch (error: any) {
      // #region agent log
      // @ts-ignore - Intentionally unused, kept for debugging
      const _errorUrl = `${getApiBaseUrl()}${endpoint}`;
      // @ts-ignore - Intentionally unused, kept for debugging
      const _errorOrigin = window.location.origin;
      // #endregion
      // Capture detailed error information
      const errorDetails: any = {
        message: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Error',
        stack: error instanceof Error ? error.stack : undefined,
        url: `${getApiBaseUrl()}${endpoint}`,
        origin: window.location.origin,
        method: 'OPTIONS'
      };
      
      // Try to get more details from the error
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        errorDetails.description = 'Network error - CORS preflight request blocked or network failure';
        errorDetails.possibleCauses = [
          'CORS not configured for this origin',
          'API Gateway OPTIONS method not configured',
          'Network connectivity issue',
          'SSL/TLS certificate issue'
        ];
      }
      
      return { success: false, error: errorDetails };
    }
  };

  const testApiEndpoint = async (endpoint: string, method: string = 'GET', body?: any, includeAuth: boolean = true): Promise<{ success: boolean; status: number; data?: any; error?: string; fullError?: any; response?: Response }> => {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      if (includeAuth) {
        const token = localStorage.getItem('token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
      
      const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      
      const data = response.ok ? await response.json().catch(() => ({})) : undefined;
      
      return {
        success: response.ok,
        status: response.status,
        data,
        response,
      };
    } catch (error: any) {
      return {
        success: false,
        status: 0,
        error: error instanceof Error ? error.message : 'Network error',
        fullError: error
      };
    }
  };

  // @ts-ignore - Intentionally unused, kept for future use
  const _testS3PresignedUrl = async (): Promise<{ success: boolean; error?: any; data?: any; status?: number; isLimitError?: boolean }> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return { success: false, error: 'No token available' };
      }
      
      const response = await fetch(`${getApiBaseUrl()}/resumes/presigned-url`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const data = response.ok ? await response.json().catch(() => ({})) : undefined;
      
      return {
        success: response.ok,
        status: response.status,
        data,
      };
    } catch (error: any) {
      // Check if this is a 403 limit error (expected behavior when user has reached their limit)
      const isLimitError = error?.status === 403 || 
                          (error?.message && (
                            error.message.includes('limit') || 
                            error.message.includes('403') ||
                            error.message.includes('Resume limit reached')
                          ));
      
      return { 
        success: false, 
        error, 
        status: error?.status,
        isLimitError 
      };
    }
  };

  const runHealthChecks = async () => {
    setIsRunning(true);
    setResults([]);
    setOverallStatus('running');

    // Authentication & Cognito Tests
    const authTestStart = Date.now();
    addResult({ category: 'Authentication', test: 'User Authentication Status', status: 'running', message: 'Checking authentication...' });
    if (isAuthenticated && profile) {
      updateResult('Authentication', 'User Authentication Status', { 
        status: 'success', 
        message: `Authenticated as ${profile.email}`,
        duration: Date.now() - authTestStart
      });
    } else {
      updateResult('Authentication', 'User Authentication Status', { 
        status: 'error', 
        message: 'Not authenticated',
        error: { message: 'User is not authenticated. Please log in to run authenticated tests.' },
        duration: Date.now() - authTestStart
      });
    }

    const tokenTestStart = Date.now();
    addResult({ category: 'Authentication', test: 'JWT Token Validation', status: 'running', message: 'Validating token...' });
    try {
      // #region agent log
      // #endregion
      const tokenResult = await testApiEndpoint('/auth/verify-token', 'GET');
      if (tokenResult.success) {
        updateResult('Authentication', 'JWT Token Validation', { 
          status: 'success', 
          message: 'Token is valid',
          duration: Date.now() - tokenTestStart
        });
      } else {
        updateResult('Authentication', 'JWT Token Validation', { 
          status: 'error', 
          message: tokenResult.error || 'Token validation failed',
          error: {
            message: tokenResult.error || 'Token validation failed',
            status: tokenResult.status,
            fullError: tokenResult.fullError
          },
          duration: Date.now() - tokenTestStart
        });
      }
    } catch (error: any) {
      // #region agent log
      // #endregion
      updateResult('Authentication', 'JWT Token Validation', { 
        status: 'error', 
        message: error instanceof Error ? error.message : 'Token validation failed',
        error: {
          message: error instanceof Error ? error.message : 'Token validation failed',
          stack: error instanceof Error ? error.stack : undefined,
          code: error?.code,
          status: error?.status,
          response: error?.response,
          fullError: error
        },
        duration: Date.now() - tokenTestStart
      });
    }

    const profileTestStart = Date.now();
    addResult({ category: 'Authentication', test: 'Profile Retrieval', status: 'running', message: 'Fetching profile...' });
    try {
      const profileResult = await testApiEndpoint('/profiles/me', 'GET');
      if (profileResult.success) {
        updateResult('Authentication', 'Profile Retrieval', { 
          status: 'success', 
          message: 'Profile retrieved successfully',
          duration: Date.now() - profileTestStart
        });
      } else {
        updateResult('Authentication', 'Profile Retrieval', { 
          status: 'error', 
          message: profileResult.error || 'Profile retrieval failed',
          error: {
            message: profileResult.error || 'Profile retrieval failed',
            status: profileResult.status,
            fullError: profileResult.fullError
          },
          duration: Date.now() - profileTestStart
        });
      }
    } catch (error: any) {
      updateResult('Authentication', 'Profile Retrieval', { 
        status: 'error', 
        message: error instanceof Error ? error.message : 'Profile retrieval failed',
        error: {
          message: error instanceof Error ? error.message : 'Profile retrieval failed',
          stack: error instanceof Error ? error.stack : undefined,
          code: error?.code,
          status: error?.status,
          response: error?.response,
          fullError: error
        },
        duration: Date.now() - profileTestStart
      });
    }

    // API Gateway CORS Tests - All endpoints from infrastructure/cloudformation/templates/kairos-phase5-api-workers.yaml
    // These are base endpoints that should have OPTIONS methods for CORS preflight
    const endpoints = [
      // Auth endpoints
      '/auth/verify',
      '/auth/resend-verification',
      '/auth/verify-token',
      // Service endpoints (base resources)
      '/service-plans',
      '/service-plans/user/subscription', // Specific sub-path
      '/resumes',
      '/job-applications',
      '/openai',
      '/profiles',
      '/company-intel',
      '/interview-prep',
      '/intelligent-search',
      // Health endpoint
      '/health/system'
    ];

    for (const endpoint of endpoints) {
      const corsTestStart = Date.now();
      addResult({ category: 'API Gateway', test: `CORS Preflight: ${endpoint}`, status: 'running', message: 'Testing CORS...' });
      const corsResult = await testCorsPreflight(endpoint);
      if (corsResult.success) {
        updateResult('API Gateway', `CORS Preflight: ${endpoint}`, {
          status: 'success',
          message: 'CORS preflight successful',
          details: corsResult.details,
          duration: Date.now() - corsTestStart
        });
      } else {
        updateResult('API Gateway', `CORS Preflight: ${endpoint}`, {
          status: 'error',
          message: corsResult.error?.message || 'CORS preflight failed',
          error: {
            message: corsResult.error?.message || 'CORS preflight request failed',
            description: corsResult.error?.description,
            possibleCauses: corsResult.error?.possibleCauses,
            stack: corsResult.error?.stack,
            url: corsResult.error?.url,
            origin: corsResult.error?.origin,
            response: corsResult.response ? {
              status: corsResult.response.status,
              statusText: corsResult.response.statusText,
              headers: Object.fromEntries(corsResult.response.headers.entries())
            } : undefined,
            details: corsResult.details,
            fullError: corsResult.error
          },
          duration: Date.now() - corsTestStart
        });
      }
    }

    // API Gateway Endpoint Tests
    const unauthTestStart = Date.now();
    addResult({ category: 'API Gateway', test: 'Unauthorized Access (401)', status: 'running', message: 'Testing unauthorized access...' });
    // #region agent log
    // #endregion
    const unauthResult = await testApiEndpoint('/service-plans/user/subscription', 'GET', undefined, false);
    // #region agent log
    // #endregion
    if (unauthResult.status === 401 || unauthResult.status === 403) {
      updateResult('API Gateway', 'Unauthorized Access (401)', { 
        status: 'success', 
        message: `Correctly returned ${unauthResult.status}`,
        details: { status: unauthResult.status, data: unauthResult.data },
        duration: Date.now() - unauthTestStart
      });
    } else {
      updateResult('API Gateway', 'Unauthorized Access (401)', { 
        status: 'warning', 
        message: `Expected 401/403, got ${unauthResult.status}`,
        error: {
          message: `Expected 401/403, but got ${unauthResult.status}`,
          status: unauthResult.status,
          response: unauthResult.fullError,
          fullError: unauthResult
        },
        duration: Date.now() - unauthTestStart
      });
    }

    if (isAuthenticated) {
      const subTestStart = Date.now();
      addResult({ category: 'API Gateway', test: 'Authenticated Request: Subscription', status: 'running', message: 'Testing authenticated endpoint...' });
      const authResult = await testApiEndpoint('/service-plans/user/subscription', 'GET');
      if (authResult.success) {
        updateResult('API Gateway', 'Authenticated Request: Subscription', { 
          status: 'success', 
          message: 'Subscription retrieved successfully', 
          details: authResult.data,
          duration: Date.now() - subTestStart
        });
      } else {
        updateResult('API Gateway', 'Authenticated Request: Subscription', { 
          status: 'error', 
          message: authResult.error || 'Request failed',
          error: {
            message: authResult.error || 'Request failed',
            status: authResult.status,
            response: authResult.fullError,
            fullError: authResult
          },
          duration: Date.now() - subTestStart
        });
      }

      const resumesTestStart = Date.now();
      addResult({ category: 'API Gateway', test: 'Authenticated Request: Resumes', status: 'running', message: 'Testing resumes endpoint...' });
      const resumesResult = await testApiEndpoint('/resumes', 'GET');
      if (resumesResult.success) {
        updateResult('API Gateway', 'Authenticated Request: Resumes', { 
          status: 'success', 
          message: 'Resumes retrieved successfully', 
          details: resumesResult.data,
          duration: Date.now() - resumesTestStart
        });
      } else {
        updateResult('API Gateway', 'Authenticated Request: Resumes', { 
          status: 'error', 
          message: resumesResult.error || 'Request failed',
          error: {
            message: resumesResult.error || 'Request failed',
            status: resumesResult.status,
            response: resumesResult.fullError,
            fullError: resumesResult
          },
          duration: Date.now() - resumesTestStart
        });
      }
    }

    // S3 Tests
    const s3TestStart = Date.now();
    addResult({ category: 'S3', test: 'Presigned URL Generation', status: 'running', message: 'Testing presigned URL generation...' });
    if (isAuthenticated) {
      try {
        const s3Result = await testApiEndpoint('/s3/presigned-url', 'POST', { fileName: 'test.txt', contentType: 'text/plain' });
        if (s3Result.success) {
          updateResult('S3', 'Presigned URL Generation', { 
            status: 'success', 
            message: 'Presigned URL generated successfully',
            duration: Date.now() - s3TestStart
          });
        } else {
          updateResult('S3', 'Presigned URL Generation', { 
            status: 'error', 
            message: s3Result.error || 'S3 test failed',
            error: {
              message: s3Result.error || 'S3 test failed',
              status: s3Result.status,
              fullError: s3Result.fullError
            },
            duration: Date.now() - s3TestStart
          });
        }
      } catch (error: any) {
        updateResult('S3', 'Presigned URL Generation', { 
          status: 'error', 
          message: error instanceof Error ? error.message : 'S3 test failed',
          error: {
            message: error instanceof Error ? error.message : 'S3 test failed',
            stack: error instanceof Error ? error.stack : undefined,
            fullError: error
          },
          duration: Date.now() - s3TestStart
        });
      }
    } else {
      updateResult('S3', 'Presigned URL Generation', { 
        status: 'warning', 
        message: 'Skipped - authentication required',
        duration: Date.now() - s3TestStart
      });
    }

    // DynamoDB Tests (via API)
    if (isAuthenticated) {
      const profileDbTestStart = Date.now();
      addResult({ category: 'DynamoDB', test: 'Profile Data Retrieval', status: 'running', message: 'Testing DynamoDB profile access...' });
      try {
        const profileResult = await testApiEndpoint('/profiles/me', 'GET');
        if (profileResult.success) {
          updateResult('DynamoDB', 'Profile Data Retrieval', { 
            status: 'success', 
            message: 'Profile data retrieved successfully',
            duration: Date.now() - profileDbTestStart
          });
        } else {
          updateResult('DynamoDB', 'Profile Data Retrieval', { 
            status: 'error', 
            message: profileResult.error || 'DynamoDB access failed',
            error: {
              message: profileResult.error || 'DynamoDB access failed',
              status: profileResult.status,
              fullError: profileResult.fullError
            },
            duration: Date.now() - profileDbTestStart
          });
        }
      } catch (error: any) {
        updateResult('DynamoDB', 'Profile Data Retrieval', { 
          status: 'error', 
          message: error instanceof Error ? error.message : 'DynamoDB access failed',
          error: {
            message: error instanceof Error ? error.message : 'DynamoDB access failed',
            stack: error instanceof Error ? error.stack : undefined,
            code: error?.code,
            status: error?.status,
            response: error?.response,
            fullError: error
          },
          duration: Date.now() - profileDbTestStart
        });
      }

      const subDbTestStart = Date.now();
      addResult({ category: 'DynamoDB', test: 'Subscription Data Retrieval', status: 'running', message: 'Testing DynamoDB subscription access...' });
      try {
        const subResult = await testApiEndpoint('/service-plans/user/subscription', 'GET');
        if (subResult.success) {
          updateResult('DynamoDB', 'Subscription Data Retrieval', { 
            status: 'success', 
            message: 'Subscription data retrieved successfully',
            duration: Date.now() - subDbTestStart
          });
        } else {
          updateResult('DynamoDB', 'Subscription Data Retrieval', { 
            status: 'error', 
            message: subResult.error || 'DynamoDB access failed',
            error: {
              message: subResult.error || 'DynamoDB access failed',
              status: subResult.status,
              fullError: subResult.fullError
            },
            duration: Date.now() - subDbTestStart
          });
        }
      } catch (error: any) {
        updateResult('DynamoDB', 'Subscription Data Retrieval', { 
          status: 'error', 
          message: error instanceof Error ? error.message : 'DynamoDB access failed',
          error: {
            message: error instanceof Error ? error.message : 'DynamoDB access failed',
            stack: error instanceof Error ? error.stack : undefined,
            code: error?.code,
            status: error?.status,
            response: error?.response,
            fullError: error
          },
          duration: Date.now() - subDbTestStart
        });
      }
    }

    // Cognito Tests
    const cognitoTestStart = Date.now();
    addResult({ category: 'Cognito', test: 'Cognito Configuration', status: 'running', message: 'Checking Cognito configuration...' });
    try {
      // Check if token exists (indicates Cognito is configured)
      const token = localStorage.getItem('token');
      if (token) {
        updateResult('Cognito', 'Cognito Configuration', { 
          status: 'success', 
          message: 'Cognito token found - configuration appears valid',
          duration: Date.now() - cognitoTestStart
        });
      } else {
        updateResult('Cognito', 'Cognito Configuration', { 
          status: 'warning', 
          message: 'No token found - user may not be authenticated',
          duration: Date.now() - cognitoTestStart
        });
      }
    } catch (error: any) {
      updateResult('Cognito', 'Cognito Configuration', { 
        status: 'error', 
        message: error instanceof Error ? error.message : 'Cognito configuration check failed',
        error: {
          message: error instanceof Error ? error.message : 'Cognito configuration check failed',
          stack: error instanceof Error ? error.stack : undefined,
          fullError: error
        },
        duration: Date.now() - cognitoTestStart
      });
    }

    // Additional API Gateway Endpoint Tests (for endpoints with specific paths)
    if (isAuthenticated) {
      const additionalEndpoints = [
        { path: '/auth/verify-token', method: 'GET', name: 'Auth Verify Token' },
        { path: '/openai', method: 'GET', name: 'OpenAI Service' },
        { path: '/profiles', method: 'GET', name: 'Profiles Service' },
        { path: '/company-intel', method: 'GET', name: 'Company Intelligence' },
        { path: '/interview-prep', method: 'GET', name: 'Interview Prep' },
        { path: '/intelligent-search', method: 'GET', name: 'Intelligent Search' },
        { path: '/job-applications', method: 'GET', name: 'Job Applications' }
      ];

      for (const endpoint of additionalEndpoints) {
        const endpointTestStart = Date.now();
        addResult({ category: 'API Gateway', test: `Endpoint: ${endpoint.name}`, status: 'running', message: `Testing ${endpoint.path}...` });
        const endpointResult = await testApiEndpoint(endpoint.path, endpoint.method);
        if (endpointResult.success) {
          updateResult('API Gateway', `Endpoint: ${endpoint.name}`, { 
            status: 'success', 
            message: `${endpoint.name} endpoint accessible`,
            details: endpointResult.data,
            duration: Date.now() - endpointTestStart
          });
        } else {
          // Some endpoints might return 404 if no data, which is OK
          if (endpointResult.status === 404) {
            updateResult('API Gateway', `Endpoint: ${endpoint.name}`, { 
              status: 'warning', 
              message: `Endpoint accessible but returned 404 (no data)`,
              details: { status: endpointResult.status },
              duration: Date.now() - endpointTestStart
            });
          } else {
            updateResult('API Gateway', `Endpoint: ${endpoint.name}`, { 
              status: 'error', 
              message: endpointResult.error || 'Request failed',
              error: {
                message: endpointResult.error || 'Request failed',
                status: endpointResult.status,
                response: endpointResult.fullError,
                fullError: endpointResult
              },
              duration: Date.now() - endpointTestStart
            });
          }
        }
      }
    }

    // Infrastructure Health
    const infraTestStart = Date.now();
    addResult({ category: 'Infrastructure', test: 'API Gateway Base URL', status: 'running', message: 'Checking API Gateway URL...' });
    const apiUrl = getApiBaseUrl();
    if (apiUrl && apiUrl.includes('execute-api')) {
      updateResult('Infrastructure', 'API Gateway Base URL', { 
        status: 'success', 
        message: `API Gateway URL: ${apiUrl}`,
        details: { url: apiUrl, environment: window.location.hostname },
        duration: Date.now() - infraTestStart
      });
    } else {
      updateResult('Infrastructure', 'API Gateway Base URL', { 
        status: 'warning', 
        message: `API URL: ${apiUrl}`,
        details: { url: apiUrl, environment: window.location.hostname },
        duration: Date.now() - infraTestStart
      });
    }

    // Calculate overall status - use functional update to get latest results
    setIsRunning(false);
    // Status will be recalculated by useEffect when results change
  };

  const getStatusIcon = (status: HealthCheckResult['status']) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'running': return '🔄';
      default: return '⏳';
    }
  };

  const getStatusColor = (status: HealthCheckResult['status']) => {
    switch (status) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'running': return '#007bff';
      default: return '#6c757d';
    }
  };

  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.category]) {
      acc[result.category] = [];
    }
    acc[result.category].push(result);
    return acc;
  }, {} as Record<string, HealthCheckResult[]>);

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>🔍 System Health Check</h1>
      <p>Comprehensive health check for all system components from frontend to AWS resources.</p>

      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
        <h2>Current Status</h2>
        <p><strong>Authenticated:</strong> {isAuthenticated ? '✅ YES' : '❌ NO'}</p>
        {profile && (
          <>
            <p><strong>User:</strong> {profile.email}</p>
            <p><strong>Profile:</strong> {profile.firstName} {profile.lastName}</p>
          </>
        )}
        <p><strong>Overall Status:</strong> 
          <span style={{ 
            color: overallStatus === 'success' ? '#28a745' : overallStatus === 'error' ? '#dc3545' : '#ffc107',
            fontWeight: 'bold',
            marginLeft: '10px'
          }}>
            {overallStatus.toUpperCase()}
          </span>
        </p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        {!isAuthenticated ? (
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '5px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#856404' }}>
              ⚠️ Authentication Required
            </p>
            <p style={{ margin: '0 0 15px 0', color: '#856404' }}>
              Please log in to run health checks. Some tests require authentication (JWT token validation, profile retrieval, etc.).
            </p>
            <button
              onClick={() => setIsAuthModalOpen(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Log In
            </button>
          </div>
        ) : (
          <button
            onClick={runHealthChecks}
            disabled={isRunning}
            style={{
              padding: '12px 24px',
              backgroundColor: isRunning ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '16px',
              cursor: isRunning ? 'not-allowed' : 'pointer'
            }}
          >
            {isRunning ? 'Running Health Checks...' : 'Run Health Checks'}
          </button>
        )}
      </div>

      {results.length > 0 && (
        <div>
          <h2>Health Check Results</h2>
          {Object.entries(groupedResults).map(([category, categoryResults]) => (
            <div key={category} style={{ marginBottom: '30px' }}>
              <h3 style={{ borderBottom: '2px solid #dee2e6', paddingBottom: '10px' }}>{category}</h3>
              {categoryResults.map((result, index) => (
                <div
                  key={`${result.category}-${result.test}-${index}`}
                  style={{
                    padding: '15px',
                    marginBottom: '10px',
                    backgroundColor: '#fff',
                    border: `1px solid ${getStatusColor(result.status)}`,
                    borderRadius: '5px',
                    borderLeft: `4px solid ${getStatusColor(result.status)}`
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '20px', marginRight: '10px' }}>{getStatusIcon(result.status)}</span>
                      <strong>{result.test}</strong>
                    </div>
                    {result.duration !== undefined && (
                      <span style={{ fontSize: '12px', color: '#6c757d', marginLeft: '10px' }}>
                        {result.duration}ms
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '5px 0', color: '#6c757d' }}>{result.message}</p>
                  
                  {/* Warning Details - Always visible for warnings */}
                  {result.status === 'warning' && result.details && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#fffbf0',
                      border: '1px solid #ffc107',
                      borderRadius: '3px'
                    }}>
                      <strong style={{ color: '#856404' }}>⚠️ Warning Details:</strong>
                      <div style={{ marginTop: '5px' }}>
                        {result.details.message && (
                          <p style={{ margin: '3px 0', color: '#856404' }}>
                            <strong>Message:</strong> {result.details.message}
                          </p>
                        )}
                        {result.details.note && (
                          <p style={{ margin: '3px 0', color: '#856404', fontStyle: 'italic' }}>
                            <strong>Note:</strong> {result.details.note}
                          </p>
                        )}
                        {result.details.status && (
                          <p style={{ margin: '3px 0', color: '#856404' }}>
                            <strong>HTTP Status:</strong> {result.details.status}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Error Details - Always visible for errors */}
                  {result.status === 'error' && result.error && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#fff5f5',
                      border: '1px solid #fed7d7',
                      borderRadius: '3px'
                    }}>
                      <strong style={{ color: '#c53030' }}>Error Details:</strong>
                      <div style={{ marginTop: '5px' }}>
                        <p style={{ margin: '3px 0', color: '#742a2a' }}>
                          <strong>Message:</strong> {result.error.message}
                        </p>
                        {result.error.status && (
                          <p style={{ margin: '3px 0', color: '#742a2a' }}>
                            <strong>HTTP Status:</strong> {result.error.status}
                          </p>
                        )}
                        {result.error.code && (
                          <p style={{ margin: '3px 0', color: '#742a2a' }}>
                            <strong>Error Code:</strong> {result.error.code}
                          </p>
                        )}
                        {result.error.stack && (
                          <details style={{ marginTop: '10px' }}>
                            <summary style={{ cursor: 'pointer', color: '#c53030', fontWeight: 'bold' }}>Stack Trace</summary>
                            <pre style={{
                              marginTop: '5px',
                              padding: '10px',
                              backgroundColor: '#fff',
                              borderRadius: '3px',
                              overflow: 'auto',
                              fontSize: '11px',
                              maxHeight: '200px',
                              border: '1px solid #fed7d7'
                            }}>
                              {result.error.stack}
                            </pre>
                          </details>
                        )}
                        {result.error.response && (
                          <details style={{ marginTop: '10px' }}>
                            <summary style={{ cursor: 'pointer', color: '#c53030', fontWeight: 'bold' }}>Response Details</summary>
                            <pre style={{
                              marginTop: '5px',
                              padding: '10px',
                              backgroundColor: '#fff',
                              borderRadius: '3px',
                              overflow: 'auto',
                              fontSize: '11px',
                              maxHeight: '200px',
                              border: '1px solid #fed7d7'
                            }}>
                              {JSON.stringify(result.error.response, null, 2)}
                            </pre>
                          </details>
                        )}
                        {result.error.fullError && (
                          <details style={{ marginTop: '10px' }}>
                            <summary style={{ cursor: 'pointer', color: '#c53030', fontWeight: 'bold' }}>Full Error Object</summary>
                            <pre style={{
                              marginTop: '5px',
                              padding: '10px',
                              backgroundColor: '#fff',
                              borderRadius: '3px',
                              overflow: 'auto',
                              fontSize: '11px',
                              maxHeight: '300px',
                              border: '1px solid #fed7d7'
                            }}>
                              {JSON.stringify(result.error.fullError, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Success/Warning Details - Collapsible */}
                  {(result.details || (result.status !== 'error' && result.error)) && (
                    <details style={{ marginTop: '10px' }}>
                      <summary style={{ cursor: 'pointer', color: '#007bff' }}>
                        {result.status === 'error' ? 'Additional Details' : 'View Details'}
                      </summary>
                      <pre style={{
                        marginTop: '10px',
                        padding: '10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '3px',
                        overflow: 'auto',
                        fontSize: '12px',
                        maxHeight: '300px'
                      }}>
                        {JSON.stringify(result.details || result.error, null, 2)}
                      </pre>
                    </details>
                  )}
                  
                  {result.timestamp && (
                    <p style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                      {new Date(result.timestamp).toLocaleString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {results.length === 0 && !isRunning && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
          {isAuthenticated ? (
            <p>No health checks run yet. Click "Run Health Checks" to begin.</p>
          ) : (
            <p>Please log in to run health checks.</p>
          )}
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onLogin={login}
        onRegister={register}
        onLoginSuccess={() => {
          setIsAuthModalOpen(false);
        }}
        onRegisterSuccess={() => {
          setIsAuthModalOpen(false);
        }}
      />
    </div>
  );
};
